import axios from 'axios';
import { triggerDataSync } from './dataSync';
import { isWorkdayExemptPath } from '../workday/constants';
import { formatApiError, sanitizeApiErrorPayload } from './apiError';

// Prefer explicit env. In local CRA, default to the Express port — relative /api
// only works when setupProxy.js is loaded (requires restart after adding it).
const API_URL = process.env.REACT_APP_API_URL
    || (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)
        ? 'http://localhost:5000/api'
        : 'https://api.tauris.io/api');

/** Increment on login/logout so stale 401 responses cannot wipe a fresh session. */
let authEpoch = 0;
/** Ignore session-wiping 401s during the first moment after page load (new-tab race). */
const sessionGuardUntil = Date.now() + 1000;
export function bumpAuthEpoch() {
    authEpoch += 1;
}
export function getAuthEpoch() {
    return authEpoch;
}

// Create axios instance
const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // Fail fast offline / when DNS or API is unreachable (avoids infinite spinners)
    timeout: 15000,
    transformResponse: [(data, headers) => {
        if (data == null || data === '') return data;
        const ct = String(headers?.['content-type'] || headers?.['Content-Type'] || '').toLowerCase();
        if (typeof data === 'string') {
            const trimmed = data.trimStart();
            if (ct.includes('text/html') || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
                throw new Error(
                    'API returned HTML instead of JSON. Check that the backend is running and REACT_APP_API_URL points to it.'
                );
            }
            try {
                return JSON.parse(data);
            } catch (err) {
                if (trimmed.startsWith('<')) {
                    throw new Error(
                        'API returned HTML instead of JSON. Check that the backend is running and REACT_APP_API_URL points to it.'
                    );
                }
                throw err;
            }
        }
        return data;
    }],
});

/**
 * Extract the first meaningful path segment from an axios URL.
 * Handles "/products", "products", "/api/products", and absolute URLs.
 */
export function extractCollectionFromUrl(url) {
    if (!url || typeof url !== 'string') return null;
    try {
        let path = url;
        if (url.includes('://')) {
            path = new URL(url).pathname;
        }
        path = path.split(/[?#]/)[0];
        // Strip leading /api when present (full URLs or accidental absolute paths)
        path = path.replace(/^\/?api\//, '');
        const segments = path.split('/').filter(Boolean);
        return segments[0] || null;
    } catch {
        return null;
    }
}

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {};
        config.headers['X-Auth-Epoch'] = String(authEpoch);
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Salesman Working Distributor Session (must be in CORS allowedHeaders)
        try {
            const wd = sessionStorage.getItem('tauris.workingDistributorId');
            if (wd) {
                config.headers['X-Working-Distributor-Id'] = wd;
            }
        } catch {
            /* ignore */
        }
        // Let the browser set multipart boundary for FormData uploads.
        // AxiosHeaders may ignore bracket-delete — clear via .delete / set undefined too.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            const h = config.headers;
            if (h) {
                if (typeof h.delete === 'function') {
                    h.delete('Content-Type');
                    h.delete('content-type');
                }
                delete h['Content-Type'];
                delete h['content-type'];
                if (typeof h.set === 'function') {
                    h.set('Content-Type', undefined);
                }
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
    (response) => {
        const method = (response?.config?.method || '').toLowerCase();
        // Notify the app that data changed after any mutation call.
        if (['post', 'put', 'patch', 'delete'].includes(method)) {
            const endpoint = response?.config?.url || 'mutation';
            const collection = extractCollectionFromUrl(endpoint);
            // Only broadcast when we can identify a collection — never sync-all via null
            if (collection) {
                triggerDataSync(endpoint, collection);
            }
        }
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            const failedEpoch = Number(error.config?.headers?.['X-Auth-Epoch'] ?? authEpoch);
            if (failedEpoch !== authEpoch) {
                return Promise.reject(error);
            }
            const tokenUsed = String(error.config?.headers?.Authorization || '').replace(/^Bearer\s+/i, '');
            const currentToken = localStorage.getItem('token') || '';
            if (tokenUsed && currentToken && tokenUsed !== currentToken) {
                return Promise.reject(error);
            }
            // Protect against race: early 401s right after new-tab hydrate must not wipe session
            if (currentToken && Date.now() < sessionGuardUntil) {
                return Promise.reject(error);
            }

            const publicRoutes = ['/', '/home', '/pricing', '/community', '/contact', '/login', '/register', '/signin', '/signup', '/verify-otp', '/forgot-password', '/reset-password', '/register/company', '/register/distributor/sole', '/register/distributor/sub'];
            const currentPath = window.location.pathname;
            
            if (!publicRoutes.includes(currentPath)) {
                bumpAuthEpoch();
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('business');
                localStorage.removeItem('company');
                localStorage.removeItem('distributor');
                window.location.href = '/login';
            }
        } else if (error.response?.status === 403 && error.response?.data?.code === 'WORKDAY_GATE') {
            const redirect = error.response.data.redirectPath || '/workday/check-in';
            const currentPath = window.location.pathname;
            // Stay on workday screens and gate-exempt paths (e.g. Daily Closing).
            // Layout chrome may still hit blocked APIs (draft badges); those must not bounce the user.
            if (!isWorkdayExemptPath(currentPath) && currentPath !== redirect) {
                window.location.href = redirect;
            }
        }

        // Sanitize payload so existing `err.response?.data?.error || 'Failed…'` call sites
        // receive user-friendly text without rewriting every page.
        try {
            if (error.response?.data && typeof error.response.data === 'object') {
                error.response.data = sanitizeApiErrorPayload(
                    error.response.data,
                    error.response.status
                );
            }
            error.userMessage = formatApiError(error, 'Something went wrong. Please try again.');
        } catch {
            /* never block rejection on sanitizer failure */
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
